import { Lock, Pencil, Settings } from "lucide-react";
import { toast } from "sonner";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import Tabs from "../../lib/ui/components/tabs/Tabs";

import "./JobDetails.css";

import PageLoader from "../../components/JobDisplay/PageLoader";
import InterviewQuestions from "../../components/JobManagement/InterviewQuestions";
import JobSettings from "../../components/JobManagement/JobSettings";

import ApplicantsTab from "../../components/CandidateManagement/ApplicantsTab";
import AnalysisRubric from "../../components/JobManagement/AnalysisRubric";
import InternalRoleContext from "../../components/JobManagement/InternalRoleContext";
import JobSettingsSectionNav from "../../components/JobManagement/JobSettingsSectionNav";
import AutomaticInterviewsToggle from "../../components/JobManagement/AutomaticInterviewsToggle";

import SetupJobBanner from "../../components/JobManagement/SetupJobBanner";
import RoleDigestOverview from "../../components/JobManagement/RoleDigestOverview";
import { analysisCriteriaToDraft } from "../../components/JobManagement/analysisRubricDraft";
import PageHeader from "../../components/Navigation/PageHeader";
import RoleDetailsForm from "../../components/JobManagement/RoleDetailsForm";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import AnalysisRubricRequiredModal from "../../components/ConfirmationModal/AnalysisRubricRequiredModal";
import { getModalConfig } from "../../components/ConfirmationModal/modalHelper";

// lib
import { BodyText, Heading4, XsmallText } from "../../lib/ui/components/typography/Typography";
import Button from "../../lib/ui/components/button/Button";
import Modal from "../../lib/ui/components/Modal";
import ToggleField from "../../lib/ui/components/toggle-field/ToggleField";

import {
  JOB_DESCRIPTION,
  UPDATE_ROLE_DETAILS,
  JOBS,
  GET_JOB_MESSAGES,
  GET_CALENDAR_SETTINGS,
  UPDATE_AUTOMATIC_SCHEDULING,
  UPDATE_AUTOMATIC_INTERVIEWS,
  GET_NOTIFICATION_SETTINGS,
  UPDATE_NOTIFICATION_SETTINGS,
  FETCH_CANDIDATE_INTERVIEWS,
  CLOSE_JOB,
  OPEN_JOB,
  ME,
  SETUP_JOB,
  COMPLETE_JOB_SETUP_MANUALLY,
} from "../../lib/urls";
import { parseInterviewTypes } from "../../utils/interviewTypes";
import { ANALYSIS_RUBRIC_REQUIRED_CODE, ROLE_SETUP_REQUIRED_CODE } from "../../utils/analysisRubricPreflight";

import { useStore } from "../../store/index";
import { getPageTitle } from "../../utils/navigationUtils";

// Sections within the Role Details tab. Selecting one replaces the view.
const ROLE_SECTION = {
  details: "role-details",
  hiringPreferences: "hiring-preferences",
  evaluationRules: "evaluation-rules",
  interviewQuestions: "interview-questions",
};

const ROLE_SECTIONS = [
  { id: ROLE_SECTION.details, label: "Role Details" },
  { id: ROLE_SECTION.hiringPreferences, label: "Hiring Preferences" },
  { id: ROLE_SECTION.evaluationRules, label: "Evaluation Rules" },
  { id: ROLE_SECTION.interviewQuestions, label: "Interview Questions" },
];

function InternalOnlySectionBanner({ children }) {
  return (
    <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-gray-100 border border-gray-200 rounded-md">
      <Lock size="1em" className="text-gray-600 shrink-0" />
      <XsmallText weight="semibold">{children}</XsmallText>
    </div>
  );
}

function JobDetails() {
  const {
    uid: userId,
    token,
    company,
  } = useStore();
  const params = useParams();
  const roleNameParam = params["role-name"];
  const companyNameParam = params["company-name"];
  const jobId = params.id;

  // Check if we're in creation mode
  const isNewJobMode = window.location.pathname === '/jobs/new';
  const [roleName, setRoleName] = useState(roleNameParam || "");
  const [committedRoleName, setCommittedRoleName] = useState(roleNameParam || "");
  const [companyName, setCompanyName] = useState("");
  const [automaticInterviews, setAutomaticInterviews] = useState(false);
  const [atsJobId, setAtsJobId] = useState("");
  const [interviewEligibleStageNames, setInterviewEligibleStageNames] = useState(null);
  const [autoAdvanceTargetStage, setAutoAdvanceTargetStage] = useState("");
  const [autoAdvanceHighMatchStage, setAutoAdvanceHighMatchStage] = useState("");
  const [autoAdvanceMinMatchRating, setAutoAdvanceMinMatchRating] = useState(null);
  const [autoAdvanceHumanInterviewStage, setAutoAdvanceHumanInterviewStage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [briefDescription, setBriefDescription] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [companyLocation, setCompanyLocation] = useState("");
  const [companyValues, setCompanyValues] = useState("");
  const [companyBenefits, setCompanyBenefits] = useState("");
  const [companyAdditionalInfo, setCompanyAdditionalInfo] = useState("");
  const [interviewProcess, setInterviewProcess] = useState("");
  const [idealCandidate, setIdealCandidate] = useState("");
  const [dealbreakers, setDealbreakers] = useState("");
  const [roleDocuments, setRoleDocuments] = useState([]);
  const [internalNotes, setInternalNotes] = useState("");
  const [internalDocuments, setInternalDocuments] = useState([]);
  const [notSetup, setNotSetup] = useState(true);
  const [roleContextCharacterLimit, setRoleContextCharacterLimit] = useState(60000);
  const [preferencesRevision, setPreferencesRevision] = useState(0);
  const [isSettingUpJob, setIsSettingUpJob] = useState(false);
  const [rubric, setRubric] = useState(null);
  const [isGeneratingRules, setIsGeneratingRules] = useState(false);
  const [analysisRubricWarningOpen, setAnalysisRubricWarningOpen] = useState(false);
  const [activeRoleSection, setActiveRoleSection] = useState(ROLE_SECTION.details);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [interviewQuestions, setInterviewQuestions] = useState([]);
  const [displaySection, setDisplaySection] = useState("jobDescription");

  const [isEditingBriefDescription, setIsEditingBriefDescription] =
    useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [rolePurpose, setRolePurpose] = useState("new_applicants");

  const [newCandidatesCount, setNewCandidatesCount] = useState(0);
  const [candidates, setCandidates] = useState([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [isRoleEdited, setIsRoleEdited] = useState(false);
  const [displayDescription, setDisplayDescription] = useState("");

  const [introMessage, setIntroMessage] = useState("");
  const [outroMessage, setOutroMessage] = useState("");
  // Sourced from the backend (message_templates.py) so this can never drift
  // out of sync with the copy actually sent when a job has no custom message.
  const [defaultIntroMessage, setDefaultIntroMessage] = useState("");
  const [defaultOutroMessage, setDefaultOutroMessage] = useState("");
  const [calendarMessage, setCalendarMessage] = useState("");
  const [calendarReminderMessage, setCalendarReminderMessage] = useState("");
  const [defaultCalendarMessage, setDefaultCalendarMessage] = useState("");
  const [defaultCalendarReminderMessage, setDefaultCalendarReminderMessage] = useState("");
  const [showIntroDropdown, setShowIntroDropdown] = useState(false);
  const [showOutroDropdown, setShowOutroDropdown] = useState(false);
  const [shortId, setShortId] = useState("");
  const [jobCode, setJobCode] = useState("");
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);


  // Add state to track original values and changes
  const [originalIntroMessage, setOriginalIntroMessage] = useState("");
  const [originalOutroMessage, setOriginalOutroMessage] = useState("");
  const [originalCalendarMessage, setOriginalCalendarMessage] = useState("");
  const [originalCalendarReminderMessage, setOriginalCalendarReminderMessage] = useState("");
  const [introMessageChanged, setIntroMessageChanged] = useState(false);
  const [outroMessageChanged, setOutroMessageChanged] = useState(false);

  // Calendar Settings state
  const [calendarLink, setCalendarLink] = useState("");
  const [calendarMatchRating, setCalendarMatchRating] = useState(4);
  const [originalCalendarLink, setOriginalCalendarLink] = useState("");
  const [originalCalendarMatchRating, setOriginalCalendarMatchRating] = useState(4);
  const [automaticScheduling, setAutomaticScheduling] = useState(false);
  const [emailNotificationEnabled, setEmailNotificationEnabled] = useState(true);

  const [allowedInterviewTypes, setAllowedInterviewTypes] = useState(["TEXT"]);
  const [companyDefaultInterviewTypes, setCompanyDefaultInterviewTypes] = useState(["TEXT"]);

  const [hasUnsavedQuestions, setHasUnsavedQuestions] = useState(false);
  const [hasDraftQuestions, setHasDraftQuestions] = useState(false);
  const [hasUnsavedRoleDetails, setHasUnsavedRoleDetails] = useState(false);
  const [unsavedGuard, setUnsavedGuard] = useState("questions");
  const [roleSetupWarningOpen, setRoleSetupWarningOpen] = useState(false);
  const questionsRef = useRef(null);
  const roleDetailsRef = useRef(null);
  const hasUnsavedRoleDetailsRef = useRef(false);
  hasUnsavedRoleDetailsRef.current = hasUnsavedRoleDetails;
  const [roleFooterHost, setRoleFooterHost] = useState(null);
  const roleFooterFor = (sectionId) =>
    activeRoleSection === sectionId ? roleFooterHost : null;

  // Auto-populate company name from user's organization when in new job mode
  useEffect(() => {
    if (isNewJobMode && company?.name) {
      setCompanyName(company.name);
    }
  }, [isNewJobMode, company]);

  useEffect(() => {
    if (isNewJobMode) {
      const fetchCompanyDefaults = async () => {
        try {
          const response = await fetch(ME, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            const defaults = parseInterviewTypes(data.company?.default_interview_types);
            setCompanyDefaultInterviewTypes(defaults);
            setAllowedInterviewTypes(defaults);
          }
        } catch (error) {
          console.error("Error fetching company defaults:", error);
        }
      };
      fetchCompanyDefaults();
    }
  }, [isNewJobMode, token]);

  const [status, setStatus] = useState("");
  const [jobStatusUpdating, setJobStatusUpdating] = useState(false);
  const [isCreatingJob, setIsCreatingJob] = useState(false);

  // Role Details and Settings open in large modals from the page title row.
  const [roleModalOpen, setRoleModalOpen] = useState(isNewJobMode);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [searchParams] = useSearchParams();
  const [targetSessionId, setTargetSessionId] = useState(null);
  const pageTabsRef = useRef(null);
  const [roleTabIndex, setRoleTabIndex] = useState(0);
  const appliedInitialSessionTab = useRef(false);

  const headerTitle =
    getPageTitle(location.pathname, committedRoleName) || committedRoleName || "Roles";

  const handleJobStatusToggle = useCallback(async () => {
    if (!jobId || jobId === "undefined" || !company?.id) {
      return;
    }

    setJobStatusUpdating(true);
    const isOpen = status === "OPEN";
    const url = isOpen
      ? CLOSE_JOB(company.id, jobId)
      : OPEN_JOB(company.id, jobId);

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to update job status");
      }

      setStatus(isOpen ? "CLOSED" : "OPEN");
      toast.success(
        isOpen
          ? "Role deactivated successfully"
          : "Role activated successfully"
      );
    } catch (error) {
      toast.error(
        isOpen
          ? "Failed to deactivate role. Please try again."
          : "Failed to activate role. Please try again."
      );
    } finally {
      setJobStatusUpdating(false);
    }
  }, [company?.id, jobId, status, token]);



  const roleContextCharacterCount = useMemo(() => {
    const documentCharacters = [...roleDocuments, ...internalDocuments].reduce(
      (total, document) => total + (document.character_count || 0),
      0
    );
    return idealCandidate.length + dealbreakers.length + internalNotes.length + documentCharacters;
  }, [idealCandidate, dealbreakers, internalNotes, roleDocuments, internalDocuments]);

  const analysisRoleInputs = useMemo(
    () => ({
      description: briefDescription,
      idealCandidate,
      dealbreakers,
      internalNotes,
      roleDocuments: roleDocuments.map((document) => document.id),
      internalDocuments: internalDocuments.map((document) => document.id),
    }),
    [
      briefDescription,
      idealCandidate,
      dealbreakers,
      internalNotes,
      roleDocuments,
      internalDocuments,
    ]
  );

  const saveRoleDetails = useCallback(async () => {
    const result = await fetch(UPDATE_ROLE_DETAILS(company.id, jobId), {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        role_name: roleName,
        company_name: companyName,
        role_description: briefDescription,
        company_location: companyLocation,
        company_values: companyValues,
        company_benefits: companyBenefits,
        company_additional_info: companyAdditionalInfo,
        interview_process: interviewProcess,
        ideal_candidate: idealCandidate,
        dealbreakers,
        internal_notes: internalNotes,
      }),
    });
    const data = await result.json().catch(() => ({}));
    if (!result.ok) throw new Error(data.detail || "Failed to update role details");
    setCommittedRoleName(roleName);
    setPreferencesRevision((revision) => revision + 1);
    return data;
  }, [
    briefDescription,
    company?.id,
    companyAdditionalInfo,
    companyBenefits,
    companyLocation,
    companyName,
    companyValues,
    dealbreakers,
    idealCandidate,
    internalNotes,
    interviewProcess,
    jobId,
    roleName,
    token,
  ]);

  // Each setup section saves independently and skips data the employer already supplied.
  const runJobSetup = async () => {
    setIsSettingUpJob(true);
    try {
      await saveRoleDetails();
      const response = await fetch(SETUP_JOB(company.id, jobId), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "Role setup failed");
      setInterviewQuestions(data.interview_questions || []);
      setRubric(analysisCriteriaToDraft(data.analysis_criteria));
      setNotSetup(Boolean(data.not_setup));
      const failed = Object.entries(data.sections || {}).filter(
        ([, section]) => section.status === "failed"
      );
      if (failed.length) {
        toast.error(`Setup saved partial results. Retry ${failed.map(([name]) => name.replaceAll("_", " ")).join(" and ")}.`);
      } else {
        toast.success("Role set up");
      }
    } catch (error) {
      toast.error(error.message || "Role setup failed");
    } finally {
      setIsSettingUpJob(false);
    }
  };

  const skipJobSetup = async () => {
    setIsSettingUpJob(true);
    try {
      await saveRoleDetails();
      const response = await fetch(COMPLETE_JOB_SETUP_MANUALLY(company.id, jobId), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "Could not complete setup");
      setRubric(analysisCriteriaToDraft(data.analysis_criteria));
      setNotSetup(false);
      toast.success("Manual setup enabled");
    } catch (error) {
      toast.error(error.message || "Could not complete setup");
    } finally {
      setIsSettingUpJob(false);
    }
  };

  const startInterviewing = async () => {
    if (notSetup) {
      const response = await fetch(COMPLETE_JOB_SETUP_MANUALLY(company.id, jobId), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = data.detail;
        throw new Error(typeof detail === "string" ? detail : "Could not finish role setup");
      }
      setNotSetup(false);
      if (data.analysis_criteria) {
        setRubric(analysisCriteriaToDraft(data.analysis_criteria));
      }
    }
    fetchJobProfileInfo();
    setRoleModalOpen(false);
    setRoleTabIndex(1);
    pageTabsRef.current?.setActiveTab(1);
  };

  const handleCreateJob = async () => {
    if (!roleName || !briefDescription) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsCreatingJob(true);
    try {
      const response = await fetch(JOBS(company.id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          role_name: roleName,
          company_name: companyName,
          description: briefDescription,
          company_location: companyLocation,
          company_values: companyValues,
          company_benefits: companyBenefits,
          company_additional_info: companyAdditionalInfo,
          interview_process: interviewProcess,
          user_id: userId,
          role_purpose: rolePurpose,
          allowed_interview_types: allowedInterviewTypes,
          automatic_interviews: automaticInterviews,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create job');
      }

      const data = await response.json();
      const newJobId = data.id;

      // Show success message
      toast.success("Role created successfully!");
      navigate(`/jobs/${encodeURIComponent(roleName)}/${newJobId}?setup=1`);
    } catch (error) {
      console.error("Error creating job:", error);
      toast.error("Failed to create job");
    } finally {
      setIsCreatingJob(false);
    }
  };

  const stripHtmlTags = (html) => {
    if (!html) return '';
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  useEffect(() => {
    if (isNewJobMode) {
      setIsInitialLoading(false);
      return;
    }

    if (!jobId || jobId === 'undefined') {
      navigate('/jobs');
      return;
    }
    fetchJobProfileInfo();
  }, [jobId, isNewJobMode]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const tabParam = (searchParams.get('tab') || '').toLowerCase();
    setTargetSessionId(sessionId);
    if (isInitialLoading) return;

    // The first load of a session link (for example from a digest email)
    // still opens Applicants. Clicks on Overview set the session afterward
    // and stay on this tab.
    if (!appliedInitialSessionTab.current) {
      appliedInitialSessionTab.current = true;
      if (sessionId || tabParam === 'applicants') {
        pageTabsRef.current?.setActiveTab(1);
        setRoleTabIndex(1);
        return;
      }
    }

    if (tabParam === 'applicants') {
      pageTabsRef.current?.setActiveTab(1);
      setRoleTabIndex(1);
    }
  }, [searchParams, isInitialLoading]);

  // Roles list "Set Up Role" lands here with ?edit=1 to open the Edit Role modal.
  // Creating a role lands here with ?setup=1 so the wizard continues on the next step.
  useEffect(() => {
    if (isInitialLoading || isNewJobMode) return;
    const continueSetup = searchParams.get('setup') === '1';
    if (searchParams.get('edit') !== '1' && !continueSetup) return;
    if (continueSetup) {
      setActiveRoleSection(ROLE_SECTION.hiringPreferences);
    }
    setRoleModalOpen(true);
    const params = new URLSearchParams(searchParams);
    params.delete('edit');
    params.delete('setup');
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
  }, [searchParams, isInitialLoading, isNewJobMode, navigate, location.pathname]);

  const openApplicantFromOverview = useCallback(
    (sessionId) => {
      if (!sessionId) return;
      const params = new URLSearchParams(searchParams);
      params.set('session_id', sessionId);
      params.delete('tab');
      navigate(`${location.pathname}?${params.toString()}`);
    },
    [navigate, location.pathname, searchParams]
  );

  // Applicants are the main page content; load them once the job is ready.
  useEffect(() => {
    if (!isNewJobMode && jobId && jobId !== 'undefined' && !isInitialLoading) {
      fetchCandidates();
    }
  }, [isNewJobMode, jobId, isInitialLoading]);

  // Track changes in intro message
  useEffect(() => {
    const normalizeMessage = (msg) => (msg ? msg.trim() : "");

    setIntroMessageChanged(
      normalizeMessage(introMessage) !== normalizeMessage(originalIntroMessage)
    );
  }, [introMessage, originalIntroMessage]);

  // Track changes in outro message
  useEffect(() => {
    setOutroMessageChanged(outroMessage !== originalOutroMessage);
  }, [outroMessage, originalOutroMessage]);

  const fetchJobProfileInfo = async (retryCount = 0) => {
    try {
      const response = await fetch(`${JOBS(company.id)}/${jobId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404 || response.status === 500) {
          // If job is not ready yet and we haven't retried too many times, retry after a delay
          if (retryCount < 3) {
            console.log(`Job not ready yet (${response.status}), retrying in 2 seconds... (attempt ${retryCount + 1}/3)`);
            setTimeout(() => {
              fetchJobProfileInfo(retryCount + 1);
            }, 2000);
            return;
          } else {
            throw new Error(`Job not ready yet (${response.status})`);
          }
        }
        throw new Error('Failed to fetch job profile info');
      }

      const data = await response.json();

      if (data?.role_name && !hasUnsavedRoleDetailsRef.current) {
        setRoleName(data.role_name);
        setCommittedRoleName(data.role_name);
      }
      setCompanyName(data?.company_name || "");
      setBriefDescription(data?.role_description || "");
      setDisplayDescription(stripHtmlTags(data?.role_description) || "");
      setJobDescription(data?.generated_description || "");
      setCompanyLocation(data?.company_location || "");
      setCompanyValues(data?.company_values || "");
      setCompanyBenefits(data?.company_benefits || "");
      setCompanyAdditionalInfo(data?.company_additional_info || "");
      setInterviewProcess(data?.interview_process || "");
      setIdealCandidate(data?.ideal_candidate || "");
      setDealbreakers(data?.dealbreakers || "");
      setInternalNotes(data?.internal_notes || "");
      setPreferencesRevision((revision) => revision + 1);
      const documents = Array.isArray(data?.documents) ? data.documents : [];
      setRoleDocuments(documents.filter((document) => document.audience === "candidate_visible"));
      setInternalDocuments(documents.filter((document) => document.audience === "internal"));
      setRoleContextCharacterLimit(data?.role_context_character_limit || 60000);
      setNotSetup(Boolean(data?.not_setup));
      setRubric(analysisCriteriaToDraft(data?.analysis_criteria));
      setRolePurpose(data?.role_purpose || "new_applicants");
      setStatus(data?.status || "");
      setFollowUpQuestions(
        typeof data?.follow_up_questions === "string"
          ? JSON.parse(data?.follow_up_questions)
          : data?.follow_up_questions || []
      );
      setInterviewQuestions(
        typeof data?.interview_questions === "string"
          ? JSON.parse(data?.interview_questions)
          : data?.interview_questions || []
      );
      setShortId(data?.short_id || "");
      setJobCode(data?.job_code || "");

      const allowedTypes = parseInterviewTypes(data?.allowed_interview_types);
      setAllowedInterviewTypes(allowedTypes);

      setAutomaticInterviews(data?.automatic_interviews || false);
      setAtsJobId(data?.ats_job_id || "");
      setInterviewEligibleStageNames(
        data?.interview_eligible_stage_names ?? null
      );
      setAutoAdvanceTargetStage(data?.auto_advance_target_stage || "");
      setAutoAdvanceHighMatchStage(data?.auto_advance_high_match_stage || "");
      setAutoAdvanceMinMatchRating(data?.auto_advance_min_match_rating ?? null);
      setAutoAdvanceHumanInterviewStage(
        data?.auto_advance_human_interview_stage || ""
      );
      // setApplicantUploaded(!!data?.total_candidates || false);
    } catch (error) {
      console.error("Error fetching job profile info", error);
      toast.error("Failed to load job details. Redirecting to jobs page...");
      navigate('/jobs');
    } finally {
      setIsInitialLoading(false);
    }
  };

  const fetchCandidates = async (silent = false, signal) => {
    if (!jobId || jobId === 'undefined' || !company?.id) {
      if (!silent) {
        setIsLoadingCandidates(false);
      }
      return;
    }

    if (!silent) {
      setIsLoadingCandidates(true);
    }

    try {
      const response = await fetch(
        FETCH_CANDIDATE_INTERVIEWS(company.id, jobId),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );

      if (response.ok) {
        const candidatesData = await response.json();

        setCandidates(candidatesData);

        // Count candidates with status "new" or "NEW"
        const newCount = candidatesData.filter(candidate =>
          candidate?.status?.toLowerCase() === 'new'
        ).length;
        setNewCandidatesCount(newCount);
        return candidatesData;
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }
      console.error(silent ? "Error fetching candidates silently:" : "Error fetching candidates:", error);
      if (!silent) {
        setCandidates([]);
        setNewCandidatesCount(0);
      }
    } finally {
      if (!silent) {
        setIsLoadingCandidates(false);
      }
    }
  };

  const updateJobDetails = (followUpQuestions) => {
    setIsUpdating(true);

    const promiseResponse = async () => {
      const result = await fetch(JOBS(company.id) + "/" + jobId, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          queries: followUpQuestions,
          user_id: userId,
        }),
      });
      if (!result.ok) {
        throw new Error("Failed to update job details");
      }

      const data = await result.json();
      setJobDescription(data?.job_description);
      setInterviewQuestions(data?.interview_questions);
      setFollowUpQuestions(data?.follow_up_questions);
    };

    toast.promise(promiseResponse, {
      loading: "Updating job details",
      success: () => {
        setIsUpdating(false);
        return "Job details updated successfully";
      },
      error: "Error updating job details",
    });
  };

  const updatedJobDescription = async (newDescription) => {
    try {
      const data = await fetch(JOB_DESCRIPTION(company.id, jobId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          job_description: newDescription,
        }),
      });

      if (!data.ok) {
        const error = await data.json();
        throw new Error(error?.detail || "Failed to update job description");
      }

      const response = await data.json();
      setJobDescription(newDescription);

      return response;
    } catch (error) {
      console.error("[ERROR_UPDATE_JOB_DESCRIPTION]", error);
      throw error;
    }
  };











  const openRoleDetailsModal = () => {
    setRoleModalOpen(true);
  };

  const unsavedGuardKind = () => {
    if (hasUnsavedQuestions && hasUnsavedRoleDetails) return "both";
    if (hasUnsavedRoleDetails) return "role";
    return "questions";
  };

  // Closing the editor is guarded against unsaved role details and interview questions.
  const closeRoleDetailsModal = () => {
    if (hasUnsavedQuestions || hasUnsavedRoleDetails) {
      setUnsavedGuard(unsavedGuardKind());
      pendingNavigationRef.current = () => setRoleModalOpen(false);
      setShowUnsavedModal(true);
      return;
    }
    if (isNewJobMode) {
      navigate("/jobs");
      return;
    }
    setRoleModalOpen(false);
  };

  const openSettingsModal = () => {
    setSettingsModalOpen(true);
    if (!settingsLoaded) {
      fetchJobMessages();
      fetchCalendarSettings();
      fetchNotificationSettings();
      setSettingsLoaded(true);
    }
  };

  // Fetch job messages when the Settings modal is first opened
  const fetchJobMessages = async () => {
    setIsLoadingMessages(true);
    try {
      const response = await fetch(GET_JOB_MESSAGES(company.id, jobId), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch job messages');
      }

      const data = await response.json();

      const normalizeMessage = (msg) => (msg ? msg.trim() : "");

      // Update state with fetched messages, use defaults if null/empty
      const defaultIntroMsg = data?.default_intro_message || "";
      const defaultOutroMsg = data?.default_outro_message || "";
      const defaultCalendarMsg = data?.default_calendar_message || "";
      const defaultCalendarReminderMsg = data?.default_calendar_reminder_message || "";
      const introMsg = normalizeMessage(data?.intro_message) || defaultIntroMsg;
      const outroMsg = data?.outro_message || defaultOutroMsg;
      const calendarMsg = data?.calendar_message || defaultCalendarMsg;
      const calendarReminderMsg = data?.calendar_reminder_message || defaultCalendarReminderMsg;

      setDefaultIntroMessage(defaultIntroMsg);
      setDefaultOutroMessage(defaultOutroMsg);
      setDefaultCalendarMessage(defaultCalendarMsg);
      setDefaultCalendarReminderMessage(defaultCalendarReminderMsg);
      setIntroMessage(introMsg);
      setOutroMessage(outroMsg);
      setCalendarMessage(calendarMsg);
      setCalendarReminderMessage(calendarReminderMsg);

      // Set original values for change tracking
      setOriginalIntroMessage(introMsg);
      setOriginalOutroMessage(outroMsg);
      setOriginalCalendarMessage(calendarMsg);
      setOriginalCalendarReminderMessage(calendarReminderMsg);
      setIntroMessageChanged(false);
      setOutroMessageChanged(false);
    } catch (error) {
      console.error("Error fetching job messages", error);
      // Let ErrorBoundary handle the error
      throw error;
    } finally {
      setIsLoadingMessages(false);
    }
  };

  // Fetch calendar settings when Settings tab is loaded
  const fetchCalendarSettings = async () => {
    try {
      const response = await fetch(GET_CALENDAR_SETTINGS(company.id, jobId), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch calendar settings');
      }

      const data = await response.json();

      // Update state with fetched calendar settings, use defaults if null/empty
      const calendarLinkValue = data?.calendar_link || "";
      const calendarMatchRatingValue = data?.calendar_match_rating || 4;
      const automaticSchedulingValue = data?.automatic_scheduling ?? false;

      setCalendarLink(calendarLinkValue);
      setCalendarMatchRating(calendarMatchRatingValue);
      setAutomaticScheduling(automaticSchedulingValue);

      // Set original values for change tracking
      setOriginalCalendarLink(calendarLinkValue);
      setOriginalCalendarMatchRating(calendarMatchRatingValue);
    } catch (error) {
      console.error("Error fetching calendar settings", error);
      // Use default values if fetch fails
      setCalendarLink("");
      setCalendarMatchRating(4);
      setAutomaticScheduling(false);
      setOriginalCalendarLink("");
      setOriginalCalendarMatchRating(4);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const response = await fetch(GET_NOTIFICATION_SETTINGS(company.id, jobId), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notification settings');
      }

      const data = await response.json();
      const emailNotificationValue = data?.email_notification_enabled ?? true;

      setEmailNotificationEnabled(emailNotificationValue);
    } catch (error) {
      console.error("Error fetching notification settings", error);
      setEmailNotificationEnabled(true);
    }
  };

  const persistAutomaticInterviews = useCallback(
    async (newValue, inviteOptions = {}) => {
      if (!company?.id || !jobId) {
        throw new Error("Missing job or company");
      }
      try {
        const body = { automatic_interviews: newValue };
        if (newValue === true) {
          body.include_existing_candidates =
            !!inviteOptions.includeExistingWithoutSession;
          if (inviteOptions.interviewEligibleStageNames) {
            body.interview_eligible_stage_names =
              inviteOptions.interviewEligibleStageNames;
          }
        }
        const response = await fetch(
          UPDATE_AUTOMATIC_INTERVIEWS(company.id, jobId),
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body),
          }
        );
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          const detail = data.detail;
          const error = new Error(
            typeof detail === "object"
              ? detail.message
              : detail || "Failed to update automatic interviews"
          );
          error.code = typeof detail === "object" ? detail.code : undefined;
          throw error;
        }
        if (newValue && inviteOptions.interviewEligibleStageNames) {
          setInterviewEligibleStageNames(
            inviteOptions.interviewEligibleStageNames
          );
        }
        toast.success(
          newValue
            ? "Automatic interviews enabled"
            : "Automatic interviews disabled"
        );
      } catch (error) {
        console.error("Error updating automatic interviews:", error);
        if (error.code === ANALYSIS_RUBRIC_REQUIRED_CODE) {
          setAnalysisRubricWarningOpen(true);
        } else if (error.code === ROLE_SETUP_REQUIRED_CODE) {
          setRoleSetupWarningOpen(true);
        } else {
          toast.error(error.message || "Failed to update automatic interviews");
        }
        throw error;
      }
    },
    [company?.id, jobId, token]
  );

  const showAnalysisRubricWarning = useCallback(() => {
    setAnalysisRubricWarningOpen(true);
  }, []);

  const goToAnalysisCriteria = useCallback(() => {
    setAnalysisRubricWarningOpen(false);
    if (isNewJobMode) return;
    setActiveRoleSection(ROLE_SECTION.evaluationRules);
    setRoleModalOpen(true);
  }, [isNewJobMode]);

  const handleGeneratingRulesChange = useCallback((generating) => {
    setIsGeneratingRules(generating);
    if (generating) setActiveRoleSection(ROLE_SECTION.evaluationRules);
  }, []);

  // Handle automatic scheduling toggle
  const handleAutomaticSchedulingToggle = async (newValue) => {
    setAutomaticScheduling(newValue); // Immediately update UI

    try {
      const response = await fetch(
        UPDATE_AUTOMATIC_SCHEDULING(company.id, jobId),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            automatic_scheduling: newValue,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update automatic scheduling");
      }

      // No success toast for toggle changes
    } catch (error) {
      console.error("Error updating automatic scheduling:", error);
      toast.error("Failed to update automatic scheduling");
      // Rollback UI state
      setAutomaticScheduling(!newValue);
    }
  };

  const handleNotificationToggle = async (newValue) => {
    setEmailNotificationEnabled(newValue);

    try {
      const response = await fetch(
        UPDATE_NOTIFICATION_SETTINGS(company.id, jobId),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            email_notification_enabled: newValue,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update notification settings");
      }

      toast.success(
        newValue
          ? "Email notifications enabled"
          : "Email notifications disabled"
      );
    } catch (error) {
      console.error("Error updating notification settings", error);
      toast.error("Failed to update notification settings");
      setEmailNotificationEnabled(!newValue);
    }
  };

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const pendingNavigationRef = useRef(null);

  const hasUnsavedEdits = hasUnsavedQuestions || hasUnsavedRoleDetails;

  useEffect(() => {
    if (!hasUnsavedEdits) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
    };

    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      setUnsavedGuard(
        hasUnsavedQuestions && hasUnsavedRoleDetails
          ? "both"
          : hasUnsavedRoleDetails
            ? "role"
            : "questions"
      );
      setShowUnsavedModal(true);
      pendingNavigationRef.current = () => navigate(-1);
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [hasUnsavedEdits, hasUnsavedQuestions, hasUnsavedRoleDetails, navigate]);

  const guardedNavigate = useCallback(
    (to) => {
      if (hasUnsavedQuestions || hasUnsavedRoleDetails) {
        setUnsavedGuard(
          hasUnsavedQuestions && hasUnsavedRoleDetails
            ? "both"
            : hasUnsavedRoleDetails
              ? "role"
              : "questions"
        );
        pendingNavigationRef.current = () => navigate(to);
        setShowUnsavedModal(true);
      } else {
        navigate(to);
      }
    },
    [hasUnsavedQuestions, hasUnsavedRoleDetails, navigate]
  );

  const handleContinueWithoutSaving = useCallback(() => {
    if (questionsRef.current?.discard) {
      questionsRef.current.discard();
    }
    if (roleDetailsRef.current?.discard) {
      roleDetailsRef.current.discard();
    }
    setShowUnsavedModal(false);
    setHasUnsavedQuestions(false);
    setHasUnsavedRoleDetails(false);
    if (pendingNavigationRef.current) {
      pendingNavigationRef.current();
      pendingNavigationRef.current = null;
    }
  }, []);

  const handleGoBackAndSave = useCallback(() => {
    setShowUnsavedModal(false);
    pendingNavigationRef.current = null;
  }, []);

  const handleNavigateToJobs = useCallback(() => {
    guardedNavigate("/jobs");
  }, [guardedNavigate]);

  // Page title row: messaging + automatic interview toggles, Role Details
  // button, and a gear that opens the Settings modal.
  const roleNeedsSetup = notSetup || status === "INCOMPLETE";
  const headerActions =
    !isNewJobMode && !isInitialLoading ? (
      <>
        <ToggleField
          label="Active"
          checked={status === "OPEN"}
          disabled={jobStatusUpdating || status === "INCOMPLETE"}
          disabledTooltip={
            status === "INCOMPLETE"
              ? "Finish setting up this role before it can be active"
              : undefined
          }
          onChange={handleJobStatusToggle}
          data-cy="interview-messaging-toggle"
        />
        <BodyText className="shrink-0" aria-hidden="true">
          |
        </BodyText>
        <AutomaticInterviewsToggle
          automaticInterviews={automaticInterviews}
          setAutomaticInterviews={setAutomaticInterviews}
          onSave={persistAutomaticInterviews}
          hasAnalysisRubric={Boolean(rubric)}
          onAnalysisRubricRequired={showAnalysisRubricWarning}
          needsSetup={roleNeedsSetup}
          onSetupRequired={() => setRoleSetupWarningOpen(true)}
          companyId={company?.id}
          jobId={jobId}
          token={token}
          atsJobId={atsJobId}
          interviewEligibleStageNames={interviewEligibleStageNames}
        />
        <div className="flex items-center gap-1">
          <Button
            variant="secondary"
            leftIcon={<Pencil size={16} />}
            onClick={openRoleDetailsModal}
            data-cy="role-details-button"
          >
            {roleNeedsSetup ? "Set Up Role" : "Edit Role"}
          </Button>
          <Button
            variant="ghost"
            onClick={openSettingsModal}
            className="!p-0 [&>span]:!px-2 [&>span]:!py-1.5"
            aria-label="Role settings"
            data-cy="role-settings-button"
          >
            <Settings size={16} />
          </Button>
        </div>
      </>
    ) : null;












  return (
    <div className="w-full bg-gray-100 min-h-screen">
      <PageHeader
        title={headerTitle}
        onBack={handleNavigateToJobs}
        actions={headerActions}
      />

      {isInitialLoading ? (
        <div className="w-full flex justify-center">
          <PageLoader />
        </div>
      ) : (
        <>
          <div className="w-full mt-4 flex flex-col items-center gap-4">
              {isNewJobMode ? (
                <div className="w-[98%] border border-gray-300 rounded-lg bg-white p-8">
                  <BodyText>Finish setting up this role to start reviewing applicants.</BodyText>
                </div>
              ) : notSetup && (
                <div className="w-[98%]">
                  <SetupJobBanner
                    onSetup={runJobSetup}
                    onSkip={skipJobSetup}
                    isRunning={isSettingUpJob}
                    documentCount={roleDocuments.length + internalDocuments.length}
                  />
                </div>
              )}
              {!isNewJobMode && (
              <div className="w-[98%]">
                <Tabs
                  ref={pageTabsRef}
                  ariaLabel="Role"
                  variant="underline"
                  className="z-0"
                  onTabChange={setRoleTabIndex}
                >
                  <Tabs.Item title="Overview" active>
                    <div className="border border-gray-300 rounded-lg bg-white">
                      <RoleDigestOverview
                        companyId={company.id}
                        jobId={jobId}
                        token={token}
                        onOpenApplicant={openApplicantFromOverview}
                        applicants={candidates}
                        roleName={roleName}
                        companyName={companyName}
                        company={company}
                        onCandidatesUpdate={() => fetchCandidates(true)}
                        needsSetup={roleNeedsSetup}
                        onSetUpRole={openRoleDetailsModal}
                      />
                    </div>
                  </Tabs.Item>
                  <Tabs.Item title="Applicants">
                    <div className="border border-gray-300 rounded-lg bg-white">
                      <ApplicantsTab
                        companyId={company.id}
                        jobId={jobId}
                        applicants={candidates}
                        onCandidatesUpdate={fetchCandidates}
                        onCandidatesUpdateSilent={(signal) => fetchCandidates(true, signal)}
                        token={token}
                        status={status}
                        needsSetup={roleNeedsSetup}
                        onSetUpRole={openRoleDetailsModal}
                        shortId={shortId}
                        loading={isLoadingCandidates}
                        roleName={roleName}
                        companyName={companyName}
                        targetSessionId={targetSessionId}
                        showApplicantNavigation={roleTabIndex === 1}
                        hasAnalysisRubric={Boolean(rubric)}
                        onAnalysisRubricRequired={showAnalysisRubricWarning}
                        jobStageSettings={{
                          interview_eligible_stage_names: interviewEligibleStageNames,
                          auto_advance_target_stage: autoAdvanceTargetStage,
                          auto_advance_high_match_stage: autoAdvanceHighMatchStage,
                        }}
                      />
                    </div>
                  </Tabs.Item>
                </Tabs>
              </div>
              )}

              {/* Role Details modal: role setup sections with view-replacing subnav */}
              <Modal
                show={roleModalOpen}
                onClose={closeRoleDetailsModal}
                size="7xl"
                placement="center"
                dismissible
                className="setup-modal"
              >
                <Modal.Header className="!border-b border-gray-200">
                  <Heading4 className="!mb-0">{isNewJobMode ? "Set Up Role" : "Edit Role"}</Heading4>
                </Modal.Header>
                <Modal.Body className="setup-modal-body pt-4">
                      <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
                        <JobSettingsSectionNav
                          orientation="horizontal"
                          sections={ROLE_SECTIONS}
                          activeSectionId={activeRoleSection}
                          onSectionSelect={setActiveRoleSection}
                          disabledSectionIds={isNewJobMode ? ROLE_SECTIONS.filter((section) => section.id !== ROLE_SECTION.details).map((section) => section.id) : []}
                          className="lg:hidden"
                        />
                        <JobSettingsSectionNav
                          sections={ROLE_SECTIONS}
                          activeSectionId={activeRoleSection}
                          onSectionSelect={setActiveRoleSection}
                          disabledSectionIds={isNewJobMode ? ROLE_SECTIONS.filter((section) => section.id !== ROLE_SECTION.details).map((section) => section.id) : []}
                          className="hidden lg:sticky lg:top-0 lg:block lg:w-40 lg:shrink-0"
                        />
                        {/* Every section stays mounted (hidden via CSS) so unsaved edits survive switching. */}
                        <div className="min-w-0 flex-1">
                          <div
                            className={
                              activeRoleSection === ROLE_SECTION.details
                                ? ""
                                : "hidden"
                            }
                          >
                            <RoleDetailsForm
                              fullWidth
                              isNewJobMode={isNewJobMode}
                              roleName={roleName}
                              setRoleName={setRoleName}
                              companyName={companyName}
                              setCompanyName={setCompanyName}
                              briefDescription={briefDescription}
                              setBriefDescription={setBriefDescription}
                              displayDescription={displayDescription}
                              setDisplayDescription={setDisplayDescription}
                              companyLocation={companyLocation}
                              setCompanyLocation={setCompanyLocation}
                              companyValues={companyValues}
                              setCompanyValues={setCompanyValues}
                              companyBenefits={companyBenefits}
                              setCompanyBenefits={setCompanyBenefits}
                              companyAdditionalInfo={companyAdditionalInfo}
                              setCompanyAdditionalInfo={setCompanyAdditionalInfo}
                              interviewProcess={interviewProcess}
                              setInterviewProcess={setInterviewProcess}
                              roleDocuments={roleDocuments}
                              setRoleDocuments={setRoleDocuments}
                              rolePurpose={rolePurpose}
                              setRolePurpose={setRolePurpose}
                              isCreatingJob={isCreatingJob}
                              onCreateJob={isNewJobMode ? handleCreateJob : null}
                              onUpdateRole={isNewJobMode ? null : async () => {
                                await saveRoleDetails();
                                fetchJobProfileInfo();
                              }}
                              onAdvance={() => setActiveRoleSection(ROLE_SECTION.hiringPreferences)}
                              jobCode={jobCode}
                              automaticInterviews={automaticInterviews}
                              setAutomaticInterviews={setAutomaticInterviews}
                              onAutomaticInterviewsSave={persistAutomaticInterviews}
                              hasAnalysisRubric={Boolean(rubric)}
                              onAnalysisRubricRequired={showAnalysisRubricWarning}
                              allowedInterviewTypes={allowedInterviewTypes}
                              setAllowedInterviewTypes={setAllowedInterviewTypes}
                              companyDefaultInterviewTypes={companyDefaultInterviewTypes}
                              companyId={company?.id}
                              jobId={jobId}
                              token={token}
                              atsJobId={atsJobId}
                              interviewEligibleStageNames={interviewEligibleStageNames}
                              roleContextCharacterCount={roleContextCharacterCount}
                              roleContextCharacterLimit={roleContextCharacterLimit}
                              savedRevision={preferencesRevision}
                              footerHost={roleFooterFor(ROLE_SECTION.details)}
                              onDirtyStateChange={setHasUnsavedRoleDetails}
                              detailsRef={roleDetailsRef}
                            />
                          </div>
                          {!isNewJobMode && (
                          <>
                          <div
                            className={
                              activeRoleSection === ROLE_SECTION.hiringPreferences
                                ? ""
                                : "hidden"
                            }
                          >
                            <InternalOnlySectionBanner>
                              Nothing in this section is shared with candidates
                            </InternalOnlySectionBanner>
                            <InternalRoleContext
                              idealCandidate={idealCandidate}
                              setIdealCandidate={setIdealCandidate}
                              dealbreakers={dealbreakers}
                              setDealbreakers={setDealbreakers}
                              internalNotes={internalNotes}
                              setInternalNotes={setInternalNotes}
                              internalDocuments={internalDocuments}
                              setInternalDocuments={setInternalDocuments}
                              companyId={company.id}
                              jobId={jobId}
                              token={token}
                              roleContextCharacterCount={roleContextCharacterCount}
                              roleContextCharacterLimit={roleContextCharacterLimit}
                              preferencesRevision={preferencesRevision}
                              canGenerateRules={Boolean(briefDescription && briefDescription.trim())}
                              hasRules={Boolean(rubric)}
                              onRulesGenerated={setRubric}
                              onGeneratingRulesChange={handleGeneratingRulesChange}
                              onContinue={() => setActiveRoleSection(ROLE_SECTION.evaluationRules)}
                              onBack={() => setActiveRoleSection(ROLE_SECTION.details)}
                              footerHost={roleFooterFor(ROLE_SECTION.hiringPreferences)}
                            />
                          </div>
                          <div
                            className={
                              activeRoleSection === ROLE_SECTION.evaluationRules
                                ? ""
                                : "hidden"
                            }
                          >
                            <InternalOnlySectionBanner>
                              Nothing in this section is shared with candidates
                            </InternalOnlySectionBanner>
                            <AnalysisRubric
                              roleInputs={analysisRoleInputs}
                              rubric={rubric}
                              onRubricChange={setRubric}
                              onSaveRoleDetails={saveRoleDetails}
                              onGenerateInterviewQuestions={() => {
                                setActiveRoleSection(ROLE_SECTION.interviewQuestions);
                                questionsRef.current?.requestGenerate?.();
                              }}
                              hasInterviewQuestions={hasDraftQuestions}
                              onContinue={() => setActiveRoleSection(ROLE_SECTION.interviewQuestions)}
                              onBack={() => setActiveRoleSection(ROLE_SECTION.hiringPreferences)}
                              companyId={company.id}
                              jobId={jobId}
                              token={token}
                              isGeneratingRules={isGeneratingRules}
                              footerHost={roleFooterFor(ROLE_SECTION.evaluationRules)}
                            />
                          </div>
                          <div
                            className={
                              activeRoleSection === ROLE_SECTION.interviewQuestions
                                ? ""
                                : "hidden"
                            }
                          >
                            <InternalOnlySectionBanner>
                              Nothing in this section is shared with candidates, except the question text
                            </InternalOnlySectionBanner>
                            <InterviewQuestions
                              interviewQuestions={interviewQuestions}
                              jobId={jobId}
                              companyId={company.id}
                              onQuestionsUpdate={setInterviewQuestions}
                              onJobUpdate={fetchJobProfileInfo}
                              onCandidatesUpdate={fetchCandidates}
                              roleName={roleName}
                              companyName={companyName}
                              applicants={candidates}
                              onDirtyStateChange={setHasUnsavedQuestions}
                              onHasQuestionsChange={setHasDraftQuestions}
                              saveRef={questionsRef}
                              jobDescription={briefDescription}
                              footerHost={roleFooterFor(ROLE_SECTION.interviewQuestions)}
                              onStartInterviewing={startInterviewing}
                              onBack={() => setActiveRoleSection(ROLE_SECTION.evaluationRules)}
                              setupWizard={roleNeedsSetup}
                            />
                          </div>
                          </>
                          )}
                        </div>
                      </div>
                </Modal.Body>
                <Modal.Footer className="setup-modal-footer !border-t border-gray-200">
                  <div ref={setRoleFooterHost} className="w-full" />
                </Modal.Footer>
              </Modal>

              {!isNewJobMode && (
              /* Settings modal */
              <Modal
                show={settingsModalOpen}
                onClose={() => setSettingsModalOpen(false)}
                size="7xl"
                placement="center"
                dismissible
                className="setup-modal"
              >
                <Modal.Header className="!border-b border-gray-200">
                  <Heading4 className="!mb-0">Settings</Heading4>
                </Modal.Header>
                <Modal.Body className="setup-modal-body pt-4">
                      {isLoadingMessages ? (
                        <div className="w-full flex justify-center">
                          <PageLoader />
                        </div>
                      ) : (
                        <JobSettings
                          jobId={jobId}
                          companyId={company.id}
                          token={token}
                          introMessage={introMessage}
                          outroMessage={outroMessage}
                          defaultIntroMessage={defaultIntroMessage}
                          defaultOutroMessage={defaultOutroMessage}
                          calendarMessage={calendarMessage}
                          calendarReminderMessage={calendarReminderMessage}
                          defaultCalendarMessage={defaultCalendarMessage}
                          defaultCalendarReminderMessage={defaultCalendarReminderMessage}
                          calendarLink={calendarLink}
                          calendarMatchRating={calendarMatchRating}
                          automaticScheduling={automaticScheduling}
                          emailNotificationEnabled={emailNotificationEnabled}
                          allowedInterviewTypes={allowedInterviewTypes}
                          atsJobId={atsJobId}
                          automaticInterviews={automaticInterviews}
                          interviewEligibleStageNames={interviewEligibleStageNames}
                          autoAdvanceTargetStage={autoAdvanceTargetStage}
                          autoAdvanceHighMatchStage={autoAdvanceHighMatchStage}
                          autoAdvanceMinMatchRating={autoAdvanceMinMatchRating}
                          autoAdvanceHumanInterviewStage={autoAdvanceHumanInterviewStage}
                          onScoringRubricsRegenerated={setInterviewQuestions}
                          onStageSettingsSaved={(data) => {
                            setInterviewEligibleStageNames(
                              data?.interview_eligible_stage_names ?? []
                            );
                            setAutoAdvanceTargetStage(
                              data?.auto_advance_target_stage || ""
                            );
                            setAutoAdvanceHighMatchStage(
                              data?.auto_advance_high_match_stage || ""
                            );
                            setAutoAdvanceMinMatchRating(
                              data?.auto_advance_min_match_rating ?? null
                            );
                            setAutoAdvanceHumanInterviewStage(
                              data?.auto_advance_human_interview_stage || ""
                            );
                            if (data?.automatic_interviews === false) {
                              setAutomaticInterviews(false);
                            }
                          }}
                          onAutomaticInterviewsChange={setAutomaticInterviews}
                          onMessageSaved={(type, message) => {
                            if (type === 'intro') {
                              setIntroMessage(message);
                              setOriginalIntroMessage(message);
                              setIntroMessageChanged(false);
                            } else if (type === 'outro') {
                              setOutroMessage(message);
                              setOriginalOutroMessage(message);
                              setOutroMessageChanged(false);
                            } else if (type === 'calendar') {
                              setCalendarMessage(message);
                              setOriginalCalendarMessage(message);
                            } else if (type === 'calendarReminder') {
                              setCalendarReminderMessage(message);
                              setOriginalCalendarReminderMessage(message);
                            }
                          }}
                          onCalendarSettingsSaved={(link, rating) => {
                            setCalendarLink(link);
                            setCalendarMatchRating(rating);
                            setOriginalCalendarLink(link);
                            setOriginalCalendarMatchRating(rating);
                          }}
                          onAutomaticSchedulingChange={handleAutomaticSchedulingToggle}
                          onNotificationChange={handleNotificationToggle}
                          onInterviewTypesSaved={(types) => {
                            setAllowedInterviewTypes(types);
                            fetchJobProfileInfo();
                          }}
                        />
                      )}
                </Modal.Body>
              </Modal>
              )}
            </div>
        </>
      )
      }
      <AnalysisRubricRequiredModal
        isOpen={analysisRubricWarningOpen}
        onClose={() => setAnalysisRubricWarningOpen(false)}
        onConfirm={goToAnalysisCriteria}
        isNewRole={isNewJobMode}
      />
      <ConfirmationModal
        isOpen={roleSetupWarningOpen}
        onClose={() => setRoleSetupWarningOpen(false)}
        onConfirm={() => {
          setRoleSetupWarningOpen(false);
          openRoleDetailsModal();
        }}
        {...getModalConfig("roleSetupRequired")}
        confirmButtonVariant="primary"
      />
      <ConfirmationModal
        isOpen={showUnsavedModal}
        onClose={handleContinueWithoutSaving}
        onConfirm={handleGoBackAndSave}
        {...getModalConfig(
          unsavedGuard === "role"
            ? "unsavedRoleDetails"
            : unsavedGuard === "both"
              ? "unsavedChanges"
              : "unsavedQuestions"
        )}
        confirmButtonVariant="primary"
        dismissible={false}
      />
    </div >
  );
}

export default JobDetails;
